import { withErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { ColorNeutralWhite, ColorNordicGrey3, Spacing8, Spacing16 } from 'autogen/design-tokens/tokens';
import {
    ButtonLabel,
    FrontPage,
    HeaderLabel
} from 'autogen/translation-keys/trans-website-articleuniverse-sectionnavigationmenu';
import { Button } from 'components/Button';
import DrawerView from 'components/generic/Drawer/DrawerView';
import useDrawer from 'components/generic/Drawer/hooks/useDrawer';
import Menu from 'components/generic/menu';
import { Typography } from 'components/generic/typography';
import ContainerFluid from 'components/Layout/components/ContainerFluid';
import ListItem from 'components/List/components/ListItem/ListItem';
import { QuickViewCloseButton } from 'components/quickView/components/DefaultQuickViewComponents/Header';
import dynamic from 'helpers/dynamic';
import { bodyBackgroundColor } from '../../shared/ArticleUniverseGlobalStyling';
import type { SectionNavigationType } from '../../shared/types';
import { frontPageId } from '../../shared/variables';

const NavigationWrapper = styled(ContainerFluid)`
    padding-top: ${Spacing16};
    padding-bottom: ${Spacing16};
`;

const StyledButton = styled(Button)`
    display: flex;
    width: 100%;
    justify-content: space-between;    
`;

const StyledMenu = styled(Menu)`
    background-color: transparent;
`;

const ArrowDown = styled(dynamic(() => import('icons/ArrowDown.svg?react')))`
    width: 16px;
    height: 16px;
`;

const menuItemStyling = css`
     position: relative;
    
    &:after {
        position: absolute;
        content: '';
        left: ${Spacing16};
        right: ${Spacing16};
        bottom: 0;
        display: block;
        border-bottom: 1px solid ${ColorNordicGrey3};
    }

    &:last-child:after {
        content: none;
    }
`;

const StyledMenuItem = styled(ListItem)`
   ${menuItemStyling}
`;

const StyledAnchorMenuItem = styled(ListItem.Anchor)`
   ${menuItemStyling}
`;

const MenuContainer = styled.div`
    display: flex;
    gap: ${Spacing16};
    flex-direction: column;
    width: 100%;
`;

const MenuHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: ${ColorNeutralWhite};
    padding: ${Spacing8} ${Spacing16};
`;

const StyledDrawerView = styled(DrawerView)`
    && {
        gap: ${({ theme }) => theme.Spacing16};
        background-color: ${bodyBackgroundColor};
    }

`;

function SectionNavigationDrawer({ sections, currentSectionId, frontPageUrl }: SectionNavigationType) {
    const { t } = useTranslation();
    const drawerId = 'SectionNavigationDrawer';
    const { open, close } = useDrawer(drawerId);

    return (
        <NavigationWrapper>
            <StyledButton variant="negative" onClick={open}>
                {t(ButtonLabel)}
                <ArrowDown />
            </StyledButton>

            <StyledDrawerView
                id={drawerId}
                position="left"
                fullscreen
                headerRenderer={() => (
                    <MenuHeader>
                        <Typography.Body size="L" strong>
                            {t(HeaderLabel)}
                        </Typography.Body>
                        <QuickViewCloseButton onClose={close} />
                    </MenuHeader>
                )}
                contentRenderer={(props) => <>{props.children}</>}
            >
                <MenuContainer>
                    <StyledMenu shouldFocus>
                        <Menu.BoxedSection>
                            {frontPageUrl && (
                                <StyledAnchorMenuItem
                                    anchorProps={{ action: frontPageUrl }}
                                    selected={currentSectionId === frontPageId}
                                >
                                    {t(FrontPage)}
                                </StyledAnchorMenuItem>
                            )}

                            {sections.map((section) => {
                                const { entryId, publicName, linkAction } = section;
                                if (!linkAction) {
                                    return (
                                        <StyledMenuItem key={entryId} selected={entryId === currentSectionId}>
                                            {publicName}
                                        </StyledMenuItem>
                                    );
                                }

                                return (
                                    <StyledAnchorMenuItem
                                        key={entryId}
                                        anchorProps={{ action: linkAction }}
                                        selected={entryId === currentSectionId}
                                    >
                                        {publicName}
                                    </StyledAnchorMenuItem>
                                );
                            })}
                        </Menu.BoxedSection>
                    </StyledMenu>
                </MenuContainer>
            </StyledDrawerView>
        </NavigationWrapper>
    );
}

export default withErrorBoundary(SectionNavigationDrawer, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
